import { useState, useCallback } from 'react';

/**
 * Custom hook for managing toast notifications
 * @returns {Object} Object containing notification state and methods
 */
const useNotification = () => {
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    title: '',
    severity: 'info',
    autoHideDuration: 6000,
    position: 'top-right',
  });

  const showNotification = useCallback(
    ({
      message,
      title = '',
      severity = 'info',
      autoHideDuration = 6000,
      position = 'top-right',
    }) => {
      setNotification({
        open: true,
        message,
        title,
        severity,
        autoHideDuration,
        position,
      });
    },
    [],
  );

  const hideNotification = useCallback(() => {
    setNotification((prev) => ({
      ...prev,
      open: false,
    }));
  }, []);

  // Convenience methods for different notification types
  const showSuccess = useCallback(
    (message, title = 'Success') => {
      showNotification({
        message,
        title,
        severity: 'success',
        autoHideDuration: 4000,
      });
    },
    [showNotification],
  );

  const showError = useCallback(
    (message, title = 'Error') => {
      showNotification({
        message,
        title,
        severity: 'error',
        autoHideDuration: 8000,
      });
    },
    [showNotification],
  );

  const showWarning = useCallback(
    (message, title = 'Warning') => {
      showNotification({
        message,
        title,
        severity: 'warning',
        autoHideDuration: 6000,
      });
    },
    [showNotification],
  );

  const showInfo = useCallback(
    (message, title = 'Information') => {
      showNotification({
        message,
        title,
        severity: 'info',
        autoHideDuration: 5000,
      });
    },
    [showNotification],
  );

  return {
    notification,
    showNotification,
    hideNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };
};

export default useNotification;
